import React from 'react';
import { Text, TextStyle, TouchableOpacity } from 'react-native';
import { useTheme } from '../../theme/ThemeContext';
import { useNavigation } from '@react-navigation/native';

interface MentionTextProps {
  text: string;
  style?: TextStyle;
  numberOfLines?: number;
}

/**
 * MentionText - Parses and displays text with clickable @mentions
 * 
 * Converts: "Hello @[John Doe](user123) how are you?"
 * Displays: "Hello @John Doe how are you?" (with @John Doe clickable)
 */
const MentionText: React.FC<MentionTextProps> = ({ text, style, numberOfLines }) => {
  const { theme } = useTheme();
  const navigation = useNavigation();

  // Parse text and extract mentions
  const parseText = () => {
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    const parts: Array<{ type: 'text' | 'mention'; content: string; userId?: string }> = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.substring(lastIndex, match.index),
        });
      }

      // Add mention
      parts.push({
        type: 'mention',
        content: `@${match[1]}`, // Display name
        userId: match[2], // User ID
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex),
      });
    }

    return parts;
  };

  const handleMentionPress = (userId: string) => {
    // Navigate to user's profile
    (navigation as any).navigate('Profile', { userId });
  };

  const parts = parseText();

  if (parts.length === 0) {
    return (
      <Text style={style} numberOfLines={numberOfLines}>
        {text}
      </Text>
    );
  }

  return (
    <Text style={style} numberOfLines={numberOfLines}>
      {parts.map((part, index) => {
        if (part.type === 'mention' && part.userId) {
          return (
            <Text
              key={index}
              style={[style, { color: theme.accent, fontWeight: '600' }]}
              onPress={() => handleMentionPress(part.userId!)}
            >
              {part.content}
            </Text>
          );
        }
        return <Text key={index}>{part.content}</Text>;
      })}
    </Text>
  );
};

export default MentionText;
